import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  AccountCredentialIntegrationType,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

export interface YardiBreezeMetadata {
  configId: string // Yardi Breeze instance identifier
  defaultProperty?: string
}

/**
 * Yardi Breeze Integration API Client
 * Handles all Yardi Breeze-specific API operations
 */
export class YardiBreezeClient extends BaseApiClient {
  /**
   * Create account credentials for Yardi Breeze
   */
  async createCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: YardiBreezeMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.YARDI_BREEZE },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'POST',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error creating Yardi Breeze account credentials',
      },
    )
  }

  /**
   * [ADMIN] Create account credentials for Yardi Breeze for a specific company
   */
  async createAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: YardiBreezeMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.YARDI_BREEZE },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error creating admin Yardi Breeze account credentials',
      },
    )
  }

  /**
   * Update account credentials for Yardi Breeze
   */
  async updateCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: YardiBreezeMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.YARDI_BREEZE },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error updating Yardi Breeze account credentials',
      },
    )
  }

  /**
   * [ADMIN] Update account credentials for Yardi Breeze for a specific company
   */
  async updateAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: YardiBreezeMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.YARDI_BREEZE },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error updating admin Yardi Breeze account credentials',
      },
    )
  }

  /**
   * Disconnect Yardi Breeze integration
   */
  async disconnect(): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
          { integrationType: AccountCredentialIntegrationType.YARDI_BREEZE },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting Yardi Breeze integration',
      },
    )
  }

  /**
   * [ADMIN] Disconnect Yardi Breeze integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
      { integrationType: AccountCredentialIntegrationType.YARDI_BREEZE },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting admin Yardi Breeze integration',
      },
    )
  }
}

// Export singleton instance
export const yardiBreezeClient = new YardiBreezeClient()
